/* eslint-disable sonarjs/no-duplicated-branches */
/* eslint-disable no-empty */
function reverseString(str: string) {
  return str.split('').reverse().join('');
}

export interface PasswordStrengthConfig {
  enableLengthRule?: boolean;
  enableLowerCaseLetterRule?: boolean;
  enableUpperCaseLetterRule?: boolean;
  enableDigitRule?: boolean;
  enableSpecialCharRule?: boolean;
  min?: number;
}

export enum PasswordStrength {
  TooShort,
  VeryWeak,
  Weak,
  Good,
  Strong,
  VeryStrong,
}

const defaultPasswordStrengthConfig: PasswordStrengthConfig = {
  enableLengthRule: true,
  enableLowerCaseLetterRule: true,
  enableUpperCaseLetterRule: true,
  enableDigitRule: true,
  enableSpecialCharRule: true,
  min: 8,
};

export interface PasswordStrengthResult {
  score: number;
  complexity: PasswordStrength;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function checkPassword(
  pwd: string,
  {
    enableLengthRule,
    enableLowerCaseLetterRule,
    enableUpperCaseLetterRule,
    enableSpecialCharRule,
    enableDigitRule,
    min,
  }: PasswordStrengthConfig = defaultPasswordStrengthConfig
): PasswordStrengthResult {
  // Simultaneous variable declaration and value assignment aren't supported in IE apparently
  // so I'm forced to assign the same value individually per var to support a crappy browser *sigh*
  let nScore = 0;
  let nLength = 0;
  let nAlphaUC = 0;
  let nAlphaLC = 0;
  let nNumber = 0;
  let nSymbol = 0;
  let nMidChar = 0;
  let nRequirements = 0;
  let nUnqChar = 0;
  let nRepChar = 0;
  let nRepInc = 0;
  let nConsecAlphaUC = 0;
  let nConsecAlphaLC = 0;
  let nConsecNumber = 0;
  let nSeqAlpha = 0;
  let nSeqNumber = 0;
  let nSeqSymbol = 0;
  let nReqChar = 0;
  const nMultMidChar = 2;
  const nMultConsecAlphaUC = 2;
  const nMultConsecAlphaLC = 2;
  const nMultConsecNumber = 2;
  const nMultSeqAlpha = 3;
  const nMultSeqNumber = 3;
  const nMultSeqSymbol = 3;
  const nMultLength = 4;
  const nMultNumber = 4;
  const nMultSymbol = 6;
  let nTmpAlphaUC = -1;
  let nTmpAlphaLC = -1;
  let nTmpNumber = -1;
  let nTmpSymbol = -1;
  const sAlphas = 'abcdefghijklmnopqrstuvwxyz';
  const sNumerics = '01234567890';
  const sSymbols = ')!@#$%^&*()';
  const nMinPwdLen = min;
  let sComplexity = PasswordStrength.TooShort;

  if (pwd) {
    nScore = pwd.length * nMultLength;
    nLength = pwd.length;
    const arrPwd = pwd.replace(/\s+/g, '').split(/\s*/);
    const arrPwdLen = arrPwd.length;

    // Loop through password to check for Symbol, Numeric, Lowercase and Uppercase pattern matches
    for (let a = 0; a < arrPwdLen; a++) {
      if (arrPwd[a].match(/[A-Z]/g)) {
        if (nTmpAlphaUC !== -1 && nTmpAlphaUC + 1 === a) {
          nConsecAlphaUC++;
        }
        nTmpAlphaUC = a;
        nAlphaUC++;
      } else if (arrPwd[a].match(/[a-z]/g)) {
        if (nTmpAlphaLC !== -1 && nTmpAlphaLC + 1 === a) {
          nConsecAlphaLC++;
        }
        nTmpAlphaLC = a;
        nAlphaLC++;
      } else if (arrPwd[a].match(/[0-9]/g)) {
        if (a > 0 && a < arrPwdLen - 1) {
          nMidChar++;
        }
        if (nTmpNumber !== -1 && nTmpNumber + 1 === a) {
          nConsecNumber++;
        }
        nTmpNumber = a;
        nNumber++;
      } else if (arrPwd[a].match(/[^a-zA-Z0-9_]/g)) {
        if (a > 0 && a < arrPwdLen - 1) {
          nMidChar++;
        }
        if (nTmpSymbol !== -1 && nTmpSymbol + 1 === a) {
        }
        nTmpSymbol = a;
        nSymbol++;
      }
      // Internal loop through password to check for repeat characters
      let bCharExists = false;
      for (let b = 0; b < arrPwdLen; b++) {
        if (arrPwd[a] === arrPwd[b] && a !== b) {
          // repeat character exists
          bCharExists = true;
          /*
                    Calculate icrement deduction based on proximity to identical characters
                    Deduction is incremented each time a new match is discovered
                    Deduction amount is based on total password length divided by the
                    difference of distance between currently selected match
                    */
          nRepInc += Math.abs(arrPwdLen / (b - a));
        }
      }
      if (bCharExists) {
        nRepChar++;
        nUnqChar = arrPwdLen - nRepChar;
        nRepInc = nUnqChar ? Math.ceil(nRepInc / nUnqChar) : Math.ceil(nRepInc);
      }
    }

    // Check for sequential alpha string patterns (forward and reverse)
    for (let s = 0; s < 23; s++) {
      const sFwd = sAlphas.substring(s, s + 3);
      const sRev = reverseString(sFwd);
      if (pwd.toLowerCase().indexOf(sFwd) !== -1 || pwd.toLowerCase().indexOf(sRev) !== -1) {
        nSeqAlpha++;
      }
    }

    // Check for sequential numeric string patterns (forward and reverse)
    for (let s = 0; s < 8; s++) {
      const sFwd = sNumerics.substring(s, s + 3);
      const sRev = reverseString(sFwd);
      if (pwd.toLowerCase().indexOf(sFwd) !== -1 || pwd.toLowerCase().indexOf(sRev) !== -1) {
        nSeqNumber++;
      }
    }

    // Check for sequential symbol string patterns (forward and reverse)
    for (let s = 0; s < 8; s++) {
      const sFwd = sSymbols.substring(s, s + 3);
      const sRev = reverseString(sFwd);
      if (pwd.toLowerCase().indexOf(sFwd) !== -1 || pwd.toLowerCase().indexOf(sRev) !== -1) {
        nSeqSymbol++;
      }
    }

    // Modify overall score value based on usage vs requirements

    // General point assignment
    if (nAlphaUC > 0 && nAlphaUC < nLength) {
      nScore = nScore + (nLength - nAlphaUC) * 2;
    }
    if (nAlphaLC > 0 && nAlphaLC < nLength) {
      nScore = nScore + (nLength - nAlphaLC) * 2;
    }
    if (nNumber > 0 && nNumber < nLength) {
      nScore = nScore + nNumber * nMultNumber;
    }
    if (nSymbol > 0) {
      nScore = nScore + nSymbol * nMultSymbol;
    }
    if (nMidChar > 0) {
      nScore = nScore + nMidChar * nMultMidChar;
    }

    // Point deductions for poor practices
    if ((nAlphaLC > 0 || nAlphaUC > 0) && nSymbol === 0 && nNumber === 0) {
      // Only Letters
      nScore = nScore - nLength;
    }
    if (nAlphaLC === 0 && nAlphaUC === 0 && nSymbol === 0 && nNumber > 0) {
      // Only Numbers
      nScore = nScore - nLength;
    }
    if (nRepChar > 0) {
      // Same character exists more than once
      nScore = nScore - nRepInc;
    }
    if (nConsecAlphaUC > 0) {
      // Consecutive Uppercase Letters exist
      nScore = nScore - nConsecAlphaUC * nMultConsecAlphaUC;
    }
    if (nConsecAlphaLC > 0) {
      // Consecutive Lowercase Letters exist
      nScore = nScore - nConsecAlphaLC * nMultConsecAlphaLC;
    }
    if (nConsecNumber > 0) {
      // Consecutive Numbers exist
      nScore = nScore - nConsecNumber * nMultConsecNumber;
    }
    if (nSeqAlpha > 0) {
      // Sequential alpha strings exist (3 characters or more)
      nScore = nScore - nSeqAlpha * nMultSeqAlpha;
    }
    if (nSeqNumber > 0) {
      // Sequential numeric strings exist (3 characters or more)
      nScore = nScore - nSeqNumber * nMultSeqNumber;
    }
    if (nSeqSymbol > 0) {
      // Sequential symbol strings exist (3 characters or more)
      nScore = nScore - nSeqSymbol * nMultSeqSymbol;
    }

    // Determine if mandatory requirements have been met and set image indicators accordingly
    const arrChars = [nLength, nAlphaUC, nAlphaLC, nNumber, nSymbol];
    const arrCharsIds = [
      enableLengthRule ? 'nLength' : null,
      enableUpperCaseLetterRule ? 'nAlphaUC' : null,
      enableLowerCaseLetterRule ? 'nAlphaLC' : null,
      enableDigitRule ? 'nNumber' : null,
      enableSpecialCharRule ? 'nSymbol' : null,
    ];
    const arrCharsLen = arrChars.length;
    for (let c = 0; c < arrCharsLen; c++) {
      const minVal = arrCharsIds[c] === 'nLength' ? nMinPwdLen! - 1 : 0;
      if (arrChars[c] === minVal + 1) {
        nReqChar++;
      } else if (arrChars[c] > minVal + 1) {
        nReqChar++;
      }
    }
    nRequirements = nReqChar;
    const nMinReqChars = pwd.length >= nMinPwdLen! ? 3 : 4;
    if (nRequirements > nMinReqChars) {
      // One or more required characters exist
      nScore = nScore + nRequirements * 2;
    }

    // Determine complexity based on overall score
    if (nScore > 100) {
      nScore = 100;
    } else if (nScore < 0) {
      nScore = 0;
    }
    if (nScore >= 0 && nScore < 20) {
      sComplexity = PasswordStrength.VeryWeak;
    } else if (nScore >= 20 && nScore < 40) {
      sComplexity = PasswordStrength.Weak;
    } else if (nScore >= 40 && nScore < 60) {
      sComplexity = PasswordStrength.Good;
    } else if (nScore >= 60 && nScore < 80) {
      sComplexity = PasswordStrength.Strong;
    } else if (nScore >= 80 && nScore <= 100) {
      sComplexity = PasswordStrength.VeryStrong;
    }

    // Display updated score criteria to client
    return { score: nScore, complexity: sComplexity };
  }

  return { score: nScore, complexity: sComplexity };
}
